<script setup lang="ts">
    import { ref } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useI18n } from 'vue-i18n'
    import { fileIconForMime } from 'models'
    import {
        useChatbotStore,
        useDialogConfirm,
        useFileRow,
        type ChatbotFile,
    } from 'composables'

    const props = defineProps<{ agentId: string }>()

    const { t: $t } = useI18n({ useScope: 'global' })
    const { openDialogConfirm } = useDialogConfirm()

    const store = useChatbotStore(props.agentId)
    const { chatFiles, isLoadingChatFiles } = storeToRefs(store)
    const { showThumb, thumbUrl, onThumbError, openFile, formatDate } =
        useFileRow({
            view: store.getChatFileUrl,
            preview: store.getChatFilePreviewUrl,
        })

    const deletingId = ref<string | null>(null)

    const handleExclude = async (file: ChatbotFile) => {
        const proceed = await openDialogConfirm({
            title: $t('confirmTitle.excludeUserFile'),
            questionLabel: $t('userFiles.excludeConfirm'),
        })
        if (!proceed) {
            return
        }
        deletingId.value = file.documentId
        try {
            await store.deleteChatFile(file.documentId)
        } catch {
            // surfaced by the host's error handling
        } finally {
            deletingId.value = null
        }
    }
</script>

<template>
    <div class="flex flex-col flex-1 min-h-0 relative">
        <!-- Loading skeleton, shaped like the rows it replaces -->
        <div
            v-if="isLoadingChatFiles && chatFiles.length === 0"
            class="u-pick-list__list u-pick-list__list--fill">
            <div
                v-for="i in 4"
                :key="i"
                class="u-pick-list__row u-pick-list__row--skeleton">
                <div class="u-pick-list__entry u-pick-list__entry--roomy">
                    <div class="vv-skeleton w-36 h-36 shrink-0 rounded-md">
                        <div class="vv-skeleton__item" />
                    </div>
                    <div class="flex flex-col gap-6 flex-1 min-w-0">
                        <div
                            class="vv-skeleton h-14 rounded"
                            :class="i % 2 ? 'w-192' : 'w-128'">
                            <div class="vv-skeleton__item" />
                        </div>
                        <div class="vv-skeleton h-12 rounded w-96">
                            <div class="vv-skeleton__item" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty state -->
        <div
            v-else-if="chatFiles.length === 0"
            class="flex flex-col flex-1 items-center justify-center gap-12 p-24 text-center">
            <div
                class="w-64 h-64 rounded-full bg-surface-1 border border-surface-3 flex items-center justify-center">
                <VvIcon
                    name="ri:folder-open-line"
                    class="w-28 h-28 text-word-4" />
            </div>
            <span class="text-14 text-word-3 leading-relaxed">
                {{ $t('userFiles.chatFilesEmpty') }}
            </span>
        </div>

        <!-- File list -->
        <ul
            v-else
            class="pk-chatbot-files u-pick-list__list u-pick-list__list--fill overflow-y-auto light-scrollbar">
            <li
                v-for="file in chatFiles"
                :key="file.documentId"
                class="u-pick-list__row">
                <button
                    type="button"
                    class="u-pick-list__entry u-pick-list__entry--roomy"
                    :title="$t('userFiles.open')"
                    @click="openFile(file)">
                    <span class="pk-chatbot-files__icon">
                        <img
                            v-if="showThumb(file)"
                            :src="thumbUrl(file)"
                            loading="lazy"
                            decoding="async"
                            alt=""
                            class="pk-chatbot-files__thumb"
                            @error="onThumbError(file)" />
                        <VvIcon v-else :name="fileIconForMime(file.mimeType)" />
                    </span>
                    <span class="flex flex-col min-w-0 flex-1">
                        <span class="u-pick-list__label">
                            {{ file.name || $t('userFiles.untitled') }}
                        </span>
                        <span class="text-12 text-word-3">
                            {{ formatDate(file.createdAt) }}
                        </span>
                    </span>
                </button>
                <div class="u-pick-list__actions pk-chatbot-files__actions">
                    <VvButton
                        modifiers="action-quiet-small"
                        icon="ri:download-line"
                        :title="$t('action.download')"
                        @click="openFile(file, true)" />
                    <VvButton
                        modifiers="action-quiet-small"
                        icon="ri:delete-bin-line"
                        :title="$t('userFiles.exclude')"
                        :loading="deletingId === file.documentId"
                        :disabled="deletingId === file.documentId"
                        @click="handleExclude(file)" />
                </div>
            </li>
        </ul>
    </div>
</template>

<style lang="scss">
    // The frame, the rows and the label come from `u-pick-list`; what is left
    // here is the file tile and the pair of row actions.
    .pk-chatbot-files {
        list-style: none;
        margin: 0;
        padding: 0;

        // The box exists to clip a thumbnail and to keep the labels of rows
        // with and without one on the same left edge: with a file icon in it
        // there is nothing to draw, so it stays transparent.
        &__icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: var(--spacing-36);
            height: var(--spacing-36);
            flex-shrink: 0;
            border-radius: var(--rounded-md);
            color: var(--color-word-3);
            overflow: hidden;

            svg {
                width: var(--spacing-20);
                height: var(--spacing-20);
            }
        }

        &__thumb {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        &__actions {
            gap: var(--spacing-2);
        }
    }
</style>
